/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { TaskPaneProps } from '@ecsper/speciality-contract';
import { ReportInfo, Stage6Stubs, VersionHistory } from '@ecsper/ui-panels';
import { useToast } from '@ecsper/ui-primitives';
import type { ReportVersionSummary } from '@ecsper/vault-client';
import { type ReactElement, type ReactNode, useState } from 'react';

import { insertCprDeclaration, insertStatementOfTruth } from './commands.js';

/**
 * Expert Witness task pane for Microsoft Word.
 *
 * Composes shared panels with two speciality-specific actions:
 * "Insert Statement of Truth" and "Insert CPR 35 Declaration".
 *
 * Save state, report info, and version history are driven by
 * the shell (apps/word, Task 2.18) which wires AutoSaveLoop
 * and VaultClient into props. This component is presentational.
 */
export interface ExpertWitnessTaskPaneProps extends TaskPaneProps {
  /** Governed save controls rendered by the shell (status, Save, Create Major Version). */
  saveControls?: ReactNode;
  /** Report metadata (loaded by shell). */
  report?: {
    title: string;
    versionNumber: number;
    lastModifiedAt: string;
    status: string;
  };
  /** Version history list (loaded by shell from VaultClient). */
  versions?: readonly ReportVersionSummary[];
  /** Loading state for versions. */
  isLoadingVersions?: boolean;
  /** Refresh callback for versions. */
  onRefreshVersions?: () => void;
  /** Version number to highlight after a fresh save. */
  highlightVersionNumber?: number;
  /** Vault governance badge (shell-provided). */
  governanceBadge?: ReactNode;
  /** AI panel stack from the declarative registry (shell-provided). */
  aiPanels?: ReactNode;
}

export function ExpertWitnessTaskPane(props: ExpertWitnessTaskPaneProps): ReactElement {
  const { toast } = useToast();
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const handleInsertSoT = async (): Promise<void> => {
    setActionInFlight('sot');
    try {
      await insertStatementOfTruth();
      toast('Statement of Truth inserted and locked.', 'success');
    } catch (e) {
      toast(`Could not insert Statement of Truth: ${String((e as Error).message)}`, 'error');
    } finally {
      setActionInFlight(null);
    }
  };

  const handleInsertDeclaration = async (): Promise<void> => {
    setActionInFlight('declaration');
    try {
      await insertCprDeclaration();
      toast('CPR Part 35 declaration inserted and locked.', 'success');
    } catch (e) {
      toast(`Could not insert declaration: ${String((e as Error).message)}`, 'error');
    } finally {
      setActionInFlight(null);
    }
  };

  return (
    <div className="ecs-ew-taskpane">
      {props.report !== undefined ? (
        <ReportInfo
          title={props.report.title}
          versionNumber={props.report.versionNumber}
          lastModifiedAt={props.report.lastModifiedAt}
          status={props.report.status}
        />
      ) : null}
      {props.governanceBadge ?? null}
      {props.saveControls ?? null}
      <section className="ecs-ew-actions">
        <button
          type="button"
          className="ecs-btn ecs-btn--primary ecs-btn--block"
          onClick={() => {
            void handleInsertSoT();
          }}
          disabled={actionInFlight === 'sot'}
        >
          Insert Statement of Truth
        </button>
        <button
          type="button"
          className="ecs-btn ecs-btn--secondary ecs-btn--block"
          onClick={() => {
            void handleInsertDeclaration();
          }}
          disabled={actionInFlight === 'declaration'}
        >
          Insert CPR 35 Declaration
        </button>
      </section>
      <VersionHistory
        versions={[...(props.versions ?? [])]}
        {...(props.isLoadingVersions !== undefined ? { isLoading: props.isLoadingVersions } : {})}
        {...(props.onRefreshVersions !== undefined ? { onRefresh: props.onRefreshVersions } : {})}
        {...(props.highlightVersionNumber !== undefined
          ? { highlightVersionNumber: props.highlightVersionNumber }
          : {})}
      />
      {props.aiPanels ?? null}
      <Stage6Stubs layout="vertical" />
    </div>
  );
}
